import { h, createContext } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { formatCart } from 'utils/storefront/format-resource';
import { data } from 'autoprefixer';

const Context = createContext();

const CartContext = ({ children }) => {
  const [cartState, setCartState] = useState({});

  // conversions api add to cart
  const addFacebookCAPI = (quantity) => {
    const atcCapiUrl = 'https://carve-capi.herokuapp.com/add-to-cart';

    fetch(atcCapiUrl, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(quantity),
    })
      .then((res) => {
        console.log(res);
      })
      .catch((err) => {
        console.log(err);
      });
  };
  const handleExternalAddToCart = (e) => {
    cart.add(e.detail.payload);
    addFacebookCAPI(e.detail.payload.quantity);
  };
  const openCartEvent = new CustomEvent('cart:open');

  useEffect(async () => {
    const initialCart = await getCart();
    for await (const line of initialCart.lines) {
      const product = await getProduct(line.handle);
      line.product = product;
    }

    if (initialCart) return setCartState(initialCart);
  }, []);

  useEffect(() => {
    // Setup cart events for components outside of this context
    document.addEventListener('cart:add', handleExternalAddToCart);

    const cartCountEl = document.querySelector('[data-cart-count]');
    cartCountEl.textContent = cartState?.item_count;

    // Cleanup
    return () =>
      document.removeEventListener('cart:add', handleExternalAddToCart);
  }, [cartState]);

  /**
   * Gets cart
   * @returns formatted cart object
   */
  const getCart = async () => {
    const cart = await fetch('/cart.js')
      .then((cartData) => cartData.json())
      .then((response) => {
        if (response) return formatCart(response);
      });
    return cart;
  };

  /**
   * Gets cart
   * @param {string} handle of product
   * @returns product object
   */
  const getProduct = async (handle) => {
    const cart = await fetch(`/products/${handle}.js`)
      .then((productData) => productData.json())
      .then((response) => {
        if (response) return response;
      });
    return cart;
  };

  /**
   * Method for handling all cart requests (add/update)
   * @param {array} lines
   * @param {array} lineIds
   * @param {string} query
   * @returns cart object (stored in state)
   */
  const updateCartLines = async (payload, query, isBackgroundJob = false) => {
    const cartUrl = `${window.Shopify.routes.root}${`cart/${query}.js`}`;
    const formData =
      query === 'add'
        ? {
            items: [
              {
                id: payload.id,
                quantity: payload.quantity,
                properties: payload.properties,
              },
            ],
          }
        : {
            updates: { [payload.id]: payload.quantity },
          };

    fetch(cartUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    })
      .then((data) => data.json())
      .then(async (response) => {
        const newCart = await getCart();
        const updatedCart = formatCart(newCart);

        for await (const line of updatedCart.lines) {
          const product = await getProduct(line.handle);
          line.product = product;
        }

        // Update cart state
        if (!isBackgroundJob) {
          setCartState(updatedCart);
          document.dispatchEvent(openCartEvent);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  /**
   * Wrapper for cart API to make interactions more simple
   */
  const cart = {
    add: (payload, isBackgroundJob) =>
      updateCartLines(payload, 'add', isBackgroundJob),
    update: (payload, isBackgroundJob) =>
      updateCartLines(payload, 'update', isBackgroundJob),
    state: cartState,
  };

  return <Context.Provider value={{ cart }}>{children}</Context.Provider>;
};

export const withCartContext = (Rendered) => (props) =>
  (
    <Context.Consumer>
      {(value) => <Rendered {...props} {...value} />}
    </Context.Consumer>
  );

export default CartContext;
